import { DataSourceWithBackend, config, getBackendSrv, toDataQueryResponse } from '@grafana/runtime';
import { of, merge } from 'rxjs';
import { getRequestLooper } from './requestLooper.js';
import { map } from './node_modules/rxjs/dist/esm5/internal/operators/map.js';
import { catchError } from './node_modules/rxjs/dist/esm5/internal/operators/catchError.js';

const RUNNING_STATUSES = ["started", "submitted", "running"];
const isRunning = (status = "") => RUNNING_STATUSES.includes(status);
const isCustomMeta = (meta) => {
  return !!(typeof meta === "object" && (meta == null ? void 0 : meta.hasOwnProperty("queryID")) && (meta == null ? void 0 : meta.hasOwnProperty("status")));
};
class DatasourceWithAsyncBackend extends DataSourceWithBackend {
  constructor(instanceSettings) {
    super(instanceSettings);
    this.runningQueries = {};
    this.requestCounter = 100;
    // cancel sets shouldCancel to tell requestLooper to cancel the query
    this.cancel = (target) => {
      this.storeQuery(target, { shouldCancel: true });
    };
    this.requestIdPrefix = instanceSettings.id;
  }
  query(request) {
    const targets = this.filterQuery ? request.targets.filter(this.filterQuery) : request.targets;
    if (!targets.length) {
      return of({ data: [] });
    }
    const all = [];
    for (let target of targets) {
      if (target.hide) {
        continue;
      }
      all.push(this.doSingle(target, request));
    }
    return merge(...all);
  }
  storeQuery(target, queryInfo) {
    const key = JSON.stringify(target);
    const existingQueryInfo = this.runningQueries[key] || {};
    this.runningQueries[key] = { ...existingQueryInfo, ...queryInfo };
  }
  getQuery(target) {
    const key = JSON.stringify(target);
    return this.runningQueries[key] || {};
  }
  removeQuery(target) {
    const key = JSON.stringify(target);
    delete this.runningQueries[key];
  }
  doSingle(target, request) {
    let queryID = void 0;
    let status = void 0;
    let allData = [];
    return getRequestLooper(
      { ...request, targets: [target], requestId: `${this.requestIdPrefix}_${this.requestCounter++}` },
      {
        /**
         * Additional query to execute if the current query is still in a running state
         */
        getNextQuery: (rsp) => {
          var _a, _b;
          if ((_a = rsp.data) == null ? void 0 : _a.length) {
            const first = rsp.data[0];
            const meta = (_b = first.meta) == null ? void 0 : _b.custom;
            if (isCustomMeta(meta) && isRunning(meta.status)) {
              queryID = meta.queryID;
              status = meta.status;
              this.storeQuery(target, { queryID });
              return { ...target, queryID };
            }
          }
          this.removeQuery(target);
          return void 0;
        },
        /**
         * The original request
         */
        query: (request2) => {
          const { range, targets, requestId, intervalMs, maxDataPoints } = request2;
          const [_query] = targets;
          const query = {
            ..._query,
            meta: { queryFlow: "async" }
          };
          const data = {
            queries: [
              {
                ...query,
                intervalMs,
                maxDataPoints,
                // getRef optionally chained to support < v8.3.x of Grafana
                datasource: this == null ? void 0 : this.getRef(),
                datasourceId: this.id,
                ...this.applyTemplateVariables(query, request2.scopedVars)
              }
            ],
            range,
            from: range.from.valueOf().toString(),
            to: range.to.valueOf().toString()
          };
          let headers = {};
          const cachingDisabled = !config.featureToggles.awsAsyncQueryCaching;
          if (cachingDisabled && isRunning(status)) {
            headers = { "X-Cache-Skip": true };
          }
          const options = {
            method: "POST",
            url: "/api/ds/query",
            data,
            requestId,
            headers
          };
          return getBackendSrv().fetch(options).pipe(
            map((result) => ({ data: toDataQueryResponse(result).data })),
            catchError((err) => {
              return of(toDataQueryResponse(err));
            })
          );
        },
        /**
         * Process the results
         */
        process: (data) => {
          for (const frame of data) {
            if (frame.fields.length > 0) {
              allData.push(frame);
            }
          }
          return allData;
        },
        shouldCancel: () => {
          const { shouldCancel } = this.getQuery(target);
          return !!shouldCancel;
        },
        /**
         * Callback that gets executed when unsubscribed
         */
        onCancel: () => {
          if (queryID) {
            this.removeQuery(target);
            this.postResource("cancel", {
              queryId: queryID
            }).catch((err) => {
              err.isHandled = true;
              console.error(`error cancelling query ID: ${queryID}`, err);
            });
          }
        }
      }
    );
  }
}

export { DatasourceWithAsyncBackend };
//# sourceMappingURL=DatasourceWithAsyncBackend.js.map
